import {Component, ViewChild} from '@angular/core';
import {KENDO_DROPDOWNS} from '@progress/kendo-angular-dropdowns';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';

import {BoiteDialogueGeneriqueComponent, BoiteDialogueMessagesComponent, ControleConteneurComponent, SimplePanelComponent} from '../../../../../../val-ammon-composants/val-composants-lib/src/public-api';
import {AeFMettreAJourActivationFacturesAchatParametres, AeFMettreAJourActivationFacturesAchatResultat, EFactTraitementService} from '../../../../../../val-ammon-services/val-services-efact-lib/src/public-api';
import {ValApiResultat} from '../../../../../../val-ammon-services/val-services-communs-lib/src/public-api';

//Clé de traduction du titre du traitement — repris dans la boîte de choix et dans la boîte de restitution
const c_Titre = "achat.activer-desactiver.titre";

//Traitement d’activation et de désactivation des factures d’achat (SU #117315)
//  Neutralise des lignes du SAS sans les supprimer. La recherche « Factures achats » ne filtre pas
//  IDESACTIVE : une facture désactivée reste visible dans la grille, donc réactivable par ce même
//  traitement.
@Component (
{
  selector: 'val-activer-desactiver-factures-achat',
  imports:
  [
    KENDO_DROPDOWNS,
    BoiteDialogueGeneriqueComponent,
    BoiteDialogueMessagesComponent,
    SimplePanelComponent,
    ControleConteneurComponent,
    TranslocoPipe
  ],
  templateUrl: './activer-desactiver-factures-achat.component.html',
  styleUrl: './activer-desactiver-factures-achat.component.scss'
})
export class ActiverDesactiverFacturesAchatComponent
{
  //Création
  //  Paramètres
  //    eFactTraitementService : Service e-Facture
  //    translocoService       : Service de traduction
  constructor (private eFactTraitementService: EFactTraitementService, private translocoService: TranslocoService)
  {
    this.titre = c_Titre;
    this.tailleLabels = 90;
    this.nombreFacturesSelectionnees = 0;
    //Les libellés sont traduits à chaque ouverture de la boîte de choix (ConstruireActions), dans la
    //langue active : ici, les traductions peuvent ne pas être encore chargées.
    this.actions = [];
    this.actionSelectionnee = null;
  }

  //Boîte de dialogue de choix de l’action
  @ViewChild ("boiteDialogue")
  public boiteDialogue!: BoiteDialogueGeneriqueComponent<void>;

  //Boîte de dialogue de restitution
  @ViewChild ("boiteDialogueMessages")
  public boiteDialogueMessages!: BoiteDialogueMessagesComponent;

  //Clé de traduction du titre du traitement
  public titre: string;

  //Taille des labels
  public tailleLabels: number;

  //Nombre de factures de la sélection en cours — affiché pour que l’utilisateur sache sur quoi il agit
  public nombreFacturesSelectionnees: number;

  //Actions proposées
  public actions: DonneesActionActivationFactureAchat[];

  //Action sélectionnée
  public actionSelectionnee: DonneesActionActivationFactureAchat | null;

  //Exécute le traitement
  //  Paramètres
  //    identifiantsFactures : Identifiants des factures sélectionnées dans la grille
  //  Retourne
  //    Vrai si au moins une facture a changé d’état — la grille doit alors être rafraîchie
  public async Executer (identifiantsFactures: string[]) : Promise<boolean>
  {
    let action: DonneesActionActivationFactureAchat | null;

    if (identifiantsFactures.length === 0)
      return false;

    action = await this.ChoisirAction (identifiantsFactures.length);

    if (action === null)
      return false;

    return this.Appliquer (identifiantsFactures, action);
  }

  //Ouvre la boîte de choix de l’action
  //  Paramètres
  //    nombreFactures : Nombre de factures de la sélection
  //  Retourne
  //    Action retenue, nulle si l’utilisateur a annulé
  private async ChoisirAction (nombreFactures: number) : Promise<DonneesActionActivationFactureAchat | null>
  {
    //Aucune action présélectionnée : la valider par défaut ferait de « Désactiver » un geste
    //accessible par une simple validation, sur des lignes que l’utilisateur n’a pas relues.
    this.ConstruireActions ();
    this.actionSelectionnee = null;
    this.nombreFacturesSelectionnees = nombreFactures;

    await this.boiteDialogue.Ouvrir ();

    return this.actionSelectionnee;
  }

  //Construit les actions proposées, libellées dans la langue active
  private ConstruireActions () : void
  {
    this.actions =
    [
      {libelle: this.translocoService.translate ("achat.activer-desactiver.desactiver"), desactiver: true},
      {libelle: this.translocoService.translate ("achat.activer-desactiver.activer"),    desactiver: false}
    ];
  }

  //Applique l’action sur la sélection
  //  Paramètres
  //    identifiantsFactures : Identifiants des factures sélectionnées dans la grille
  //    action               : Action retenue
  //  Retourne
  //    Vrai si au moins une facture a changé d’état
  private async Appliquer (identifiantsFactures: string[], action: DonneesActionActivationFactureAchat) : Promise<boolean>
  {
    let aefMettreAJourActivationFacturesAchatParametres: AeFMettreAJourActivationFacturesAchatParametres;
    let valApiResultat: ValApiResultat<AeFMettreAJourActivationFacturesAchatResultat>;

    try
    {
      aefMettreAJourActivationFacturesAchatParametres = new AeFMettreAJourActivationFacturesAchatParametres ();
      aefMettreAJourActivationFacturesAchatParametres.identifiantsFactures = identifiantsFactures;
      aefMettreAJourActivationFacturesAchatParametres.desactiver = action.desactiver;

      valApiResultat = await this.eFactTraitementService.MettreAJourActivationFacturesAchat (aefMettreAJourActivationFacturesAchatParametres);

      if (valApiResultat.status !== "success")
      {
        await this.Restituer ("exclamation.png",
                              [valApiResultat.message && valApiResultat.message.length > 0
                                 ? valApiResultat.message
                                 : this.translocoService.translate ("achat.commun.traitement-echoue", {titre: this.translocoService.translate (c_Titre)})]);

        return false;
      }

      await this.RestituerSucces (action, valApiResultat.resultat ?? new AeFMettreAJourActivationFacturesAchatResultat ());

      return (valApiResultat.resultat?.nombreFacturesModifiees ?? 0) > 0;
    }
    catch (erreur)
    {
      await this.RestituerErreurTechnique (erreur);

      return false;
    }
  }

  //Restitue le résultat d’un traitement réussi
  //  Paramètres
  //    action   : Action retenue
  //    resultat : Résultat du traitement
  private async RestituerSucces (action: DonneesActionActivationFactureAchat,
                                 resultat: AeFMettreAJourActivationFacturesAchatResultat) : Promise<void>
  {
    let messages: string[];
    let cleMessage: string;

    cleMessage = action.desactiver ? "achat.activer-desactiver.factures-desactivees" : "achat.activer-desactiver.factures-reactivees";

    messages = [this.translocoService.translate (cleMessage, {nombre: resultat.nombreFacturesModifiees})];

    //Les factures déjà dans l’état demandé ne sont pas retouchées : le dire évite de faire chercher
    //une trace de modification qui n’existe pas.
    if (resultat.nombreFacturesInchangees > 0)
      messages.push (this.translocoService.translate ("achat.commun.factures-inchangees", {nombre: resultat.nombreFacturesInchangees}));

    await this.Restituer ("information.png", messages);
  }

  //Restitue une erreur technique
  //  Paramètres
  //    erreur : Erreur interceptée
  private async RestituerErreurTechnique (erreur: unknown) : Promise<void>
  {
    let messages: string[];

    messages = [this.translocoService.translate ("achat.commun.erreur-technique-traitement", {titre: this.translocoService.translate (c_Titre)})];

    if (erreur != null && typeof (erreur as {message?: unknown}).message === "string")
      messages.push ((erreur as {message: string}).message);

    await this.Restituer ("exclamation.png", messages);
  }

  //Ouvre la boîte de restitution
  //  Paramètres
  //    icone    : Icone de la boîte
  //    messages : Messages à afficher
  private async Restituer (icone: string, messages: string[]) : Promise<void>
  {
    this.boiteDialogueMessages.titre = this.translocoService.translate (c_Titre);
    this.boiteDialogueMessages.icone = icone;
    this.boiteDialogueMessages.messages = messages;

    await this.boiteDialogueMessages.Ouvrir ();
  }

  //Valide le choix de l’action
  public Valider () : void
  {
    this.boiteDialogue.fonctionRetour ();
    this.boiteDialogue.Fermer ();
  }

  //Annule le choix de l’action
  public Annuler () : void
  {
    this.actionSelectionnee = null;

    this.boiteDialogue.fonctionRetour ();
    this.boiteDialogue.Fermer ();
  }
}

//Représente une action d’activation proposée à l’utilisateur
export class DonneesActionActivationFactureAchat
{
  //Libellé présenté dans la liste
  public libelle!: string;

  //Vrai si l’action désactive les factures
  public desactiver!: boolean;
}
